<template>
  <div>
    <label class="mb-1">{{ title }}</label>
    <small class="form-text text-muted">{{ subtitle }}</small>
    <div class="row">
      <div class="col">
        <input
          type="text"
          v-model="promoCode"
          @input="setPromoCode"
          :placeholder="placeholder"
          class="form-control"
          :disabled="promoCodesAlreadyUploaded"
        />
      </div>
      <div class="col-auto pt-2 px-5">or</div>
      <div class="col-auto">
        <S3FileUploader
          @uploaded="fileUploaded"
          :button-text="uploadButtonText"
          :keyPath="promoKeyPath"
          button-class="btn-primary"
        />
      </div>
    </div>
    <div v-if="type === 'in-store'" class="row mt-3">
      <div class="col">
        <label class="mb-1">Show QR Code</label>
        <small class="form-text text-muted d-block mb-2">
          Display a QR code containing the promo code for in-store redemption.
        </small>
        <div class="row">
          <div class="col-auto">
            <div class="custom-control custom-checkbox-toggle">
              <input
                class="custom-control-input"
                type="checkbox"
                :id="`show-qr-code-${type}`"
                :checked="selectPromo.showInStoreQrCode"
                @change="setShowInStoreQrCode"
              />
              <label class="custom-control-label" :for="`show-qr-code-${type}`"></label>
            </div>
          </div>
          <div class="col ml--2">
            <small class="text-muted">{{
              selectPromo.showInStoreQrCode ? 'Enabled' : 'Disabled'
            }}</small>
          </div>
        </div>
      </div>
    </div>
    <div v-if="type === 'off-premise'" class="row mt-3">
      <div class="col">
        <label class="mb-1 mt-3">Off-Premise URL</label>
        <small class="form-text text-muted">
          Redirect customers to this URL after they receive a code.
        </small>
        <input
          type="text"
          v-model="offPremiseLocationUrl"
          @input="setOffPremiseUrl"
          placeholder="https://company.com/order/online"
          class="form-control"
        />
      </div>
    </div>
  </div>
</template>

<script>
import S3FileUploader from '@/components/MainContent/S3FileUploader'
import { createNamespacedHelpers } from 'vuex'

const PromoModule = createNamespacedHelpers('promo')
const CompanyModule = createNamespacedHelpers('company')

export default {
  name: 'PromoCodeForm',
  props: ['type', 'placeholder'],
  components: {
    S3FileUploader
  },
  data: () => ({
    promoCode: '',
    offPremiseLocationUrl: ''
  }),
  computed: {
    ...PromoModule.mapGetters(['selectPromo']),
    ...CompanyModule.mapGetters(['activeCompany']),
    promoKeyPath() {
      return `campaigns/promo-codes/${this.activeCompany?._id}`
    },
    title() {
      const typeText = this.type === 'in-store' ? 'In-Store' : 'Off-Premise'
      return `${typeText} Promo Code`
    },
    promoCodesAlreadyUploaded() {
      return (
        (this.type === 'in-store' && this.selectPromo.hasUniqueInStoreCodes) ||
        (this.type === 'off-premise' && this.selectPromo.hasUniqueOffPremiseCodes)
      )
    },
    subtitle() {
      if (this.promoCodesAlreadyUploaded) {
        return 'Promo codes have already been uploaded. You may upload additional codes if needed.'
      }

      const typeText = this.type === 'in-store' ? 'in-store' : 'off-premise'
      return `Add an ${typeText} promo code to this offer or upload a CSV file of unique codes.`
    },
    uploadButtonText() {
      if (
        (this.type === 'in-store' && this.selectPromo.inStorePromoFile) ||
        (this.type === 'off-premise' && this.selectPromo.offPremisePromoFile)
      ) {
        return 'Upload Again'
      } else if (this.promoCodesAlreadyUploaded) {
        return 'Upload Additional Codes'
      } else {
        return 'Upload Promo Codes'
      }
    }
  },
  methods: {
    ...PromoModule.mapActions(['setPromo']),
    async handleBeforeUpload(file) {
      const text = await this.readFileAsText(file.data)
      const lines = text.split('\n').filter((line) => line.trim())

      if (lines.length === 0) {
        return file
      }

      const firstRow = lines[0].split(',')
      const hasHeader = this.detectHeader(firstRow, lines)

      if (hasHeader) {
        const csvWithoutHeader = lines.slice(1).join('\n')
        const blob = new Blob([csvWithoutHeader], { type: 'text/csv' })
        return {
          ...file,
          data: blob
        }
      }

      return file
    },
    detectHeader(firstRow, allLines) {
      if (allLines.length <= 1) {
        return false
      }

      const headerKeywords = [
        'id',
        '_id',
        'name',
        'email',
        'phone',
        'customer',
        'promo',
        'title',
        'description',
        'tag',
        'code'
      ]

      const hasHeaderKeyword = firstRow.some((cell) => {
        const normalized = cell.toLowerCase().trim()
        return headerKeywords.some((keyword) => normalized.includes(keyword))
      })

      if (hasHeaderKeyword) {
        return true
      }

      if (allLines.length > 1) {
        const secondRow = allLines[1].split(',')
        const firstRowNumeric = firstRow.filter((cell) => !isNaN(cell.trim())).length
        const secondRowNumeric = secondRow.filter((cell) => !isNaN(cell.trim())).length

        if (firstRowNumeric === 0 && secondRowNumeric > 0) {
          return true
        }
      }

      return false
    },
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsText(file)
      })
    },
    setPromoCode() {
      if (this.type === 'in-store') {
        this.setPromo({ ...this.selectPromo, inStorePromoFile: undefined })
        this.setPromo({ ...this.selectPromo, inStorePromoCode: this.promoCode })
      } else {
        this.setPromo({ ...this.selectPromo, offPremisePromoFile: undefined })
        this.setPromo({ ...this.selectPromo, offPremisePromoCode: this.promoCode })
      }
    },
    setOffPremiseUrl() {
      this.setPromo({ ...this.selectPromo, offPremiseLocationUrl: this.offPremiseLocationUrl })
    },
    setShowInStoreQrCode(event) {
      this.setPromo({ ...this.selectPromo, showInStoreQrCode: event.target.checked })
    },
    fileUploaded(fileUrlData) {
      const { fileName } = fileUrlData
      this.promoCode = ''
      if (this.type === 'in-store') {
        this.setPromo({ ...this.selectPromo, inStorePromoCode: undefined })
        this.setPromo({ ...this.selectPromo, inStorePromoFile: fileName })
      } else {
        this.setPromo({ ...this.selectPromo, offPremisePromoCode: undefined })
        this.setPromo({ ...this.selectPromo, offPremisePromoFile: fileName })
      }
    }
  },
  mounted() {
    this.offPremiseLocationUrl = this.selectPromo.offPremiseLocationUrl

    if (this.type === 'in-store') {
      this.promoCode = this.selectPromo.inStorePromoCode
    } else {
      this.promoCode = this.selectPromo.offPremisePromoCode
    }
  },
  watch: {
    'selectPromo.showInStoreQrCode'(newVal) {
      // Ensure the toggle reflects the store value
      if (this.type === 'in-store' && this.$el) {
        const checkbox = this.$el.querySelector(`#show-qr-code-${this.type}`)
        if (checkbox && checkbox.checked !== newVal) {
          checkbox.checked = newVal
        }
      }
    }
  }
}
</script>
